/**
 * Leave plans — named bundles of leave rules aimed at departments, locations, grades or employment
 * types, effective-dated (ADR 0032).
 *
 * A SEPARATE ROUTER, SAME `/leave` PREFIX as `leave.contract.ts`, `leave-balance-admin.contract.ts`
 * and `leave-encashment.contract.ts`, with no path overlap. Contract only: the handlers arrive in
 * follow-up changes (CLAUDE.md invariant 8).
 *
 * WHO MAY CALL: `leave.plan:read` to see plans, their rules, assignments and the explanation of
 * what applies to a person; `leave.plan:manage` to create or change them. A plan reaches many
 * people at once, so it is its own permission, not `leave.policy:manage` (which edits one leave
 * type's catalogue entry).
 *
 * THE COPILOT sees every route here, derived like any other: the reads run as the caller and the
 * writes become proposals the person confirms. Nothing is hidden — a plan rule is a configuration
 * change, reviewed on the proposal card, not a bulk act with a payload a model cannot satisfy.
 * Not financial, so no `Idempotency-Key`: setting a rule or assignment twice with the same
 * `validFrom` is refused the second time (409), never applied twice.
 *
 * PHASE 4c adds blackout periods (`/blackouts`), minimum-staffing rules (`/min-staffing`) and the
 * month grid (`/calendar/month`) to this router — operational limits on a group of people, set by
 * the same HR roles as a plan, so they reuse `leave.plan:read` / `leave.plan:manage`. The month grid
 * is HR-only (it shows staffing, not names); the employee and manager calendar stays
 * `GET /leave/calendar`. DELETE on a blackout lifts it (closes the record), it never erases it.
 * Staffing and blackout writes are not financial: no `Idempotency-Key`.
 *
 * ROUTE ORDER MATTERS for one pair: `GET /plans/effective` must be mounted before
 * `GET /plans/:id`, or the router reads "effective" as a plan id. The contract test pins it.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  createLeavePlanBodySchema,
  explainLeavePlanQuerySchema,
  getLeavePlanQuerySchema,
  leavePlanAssignmentSchema,
  leavePlanDetailSchema,
  leavePlanExplanationSchema,
  leavePlanListItemSchema,
  leavePlanRuleSchema,
  leavePlanSchema,
  listLeavePlanAssignmentsQuerySchema,
  listLeavePlansQuerySchema,
  setLeavePlanAssignmentBodySchema,
  setLeavePlanAssignmentResultSchema,
  setLeavePlanRuleBodySchema,
  updateLeavePlanBodySchema,
} from '../leave-plans.js';
import {
  createLeaveBlackoutPeriodBodySchema,
  endLeaveBlackoutPeriodResultSchema,
  getLeaveMonthCalendarQuerySchema,
  leaveBlackoutPeriodSchema,
  leaveMinStaffingRuleSchema,
  leaveMonthCalendarSchema,
  listLeaveBlackoutPeriodsQuerySchema,
  listLeaveMinStaffingRulesQuerySchema,
  setLeaveMinStaffingRuleBodySchema,
  setLeaveMinStaffingRuleResultSchema,
} from '../leave-restrictions.js';
import { errorBodySchema, paginatedSchema, uuidSchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

const planParams = z.object({ id: uuidSchema });
const planRuleParams = z.object({ id: uuidSchema, leaveTypeId: uuidSchema });
const blackoutParams = z.object({ id: uuidSchema });

export const leavePlansContract = c.router(
  {
    // ---- Plans --------------------------------------------------------------------------
    listPlans: {
      method: 'GET',
      path: '/plans',
      query: listLeavePlansQuerySchema,
      responses: {
        200: paginatedSchema(leavePlanListItemSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Leave plans with how many rules and assignments each has in force',
      metadata: routeMeta('leave.plan:read'),
    },

    /**
     * Declared BEFORE `getPlan` on purpose: `/plans/effective` would otherwise match `/plans/:id`.
     * Read-only — it explains, it never writes.
     */
    explainEffectivePlan: {
      method: 'GET',
      path: '/plans/effective',
      query: explainLeavePlanQuerySchema,
      responses: {
        200: leavePlanExplanationSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** No such person (in the caller's reach) or leave type, or the person has no employment on the date. */
        404: errorBodySchema,
      },
      summary:
        'Which plan, assignment and rule govern a person and leave type on a date, and where each value comes from',
      metadata: routeMeta('leave.plan:read'),
    },

    getPlan: {
      method: 'GET',
      path: '/plans/:id',
      pathParams: planParams,
      query: getLeavePlanQuerySchema,
      responses: {
        200: leavePlanDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'One leave plan with its rules and assignments in force on a date (or all of them)',
      metadata: routeMeta('leave.plan:read'),
    },

    createPlan: {
      method: 'POST',
      path: '/plans',
      body: createLeavePlanBodySchema,
      responses: {
        201: leavePlanSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** The name is already taken in this organisation. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Create a leave plan (leave.plan:manage)',
      metadata: routeMeta('leave.plan:manage'),
    },

    updatePlan: {
      method: 'PATCH',
      path: '/plans/:id',
      pathParams: planParams,
      body: updateLeavePlanBodySchema,
      responses: {
        200: leavePlanSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Rename a plan, change its description or move it between draft, active and archived (leave.plan:manage)',
      metadata: routeMeta('leave.plan:manage'),
    },

    // ---- Rules --------------------------------------------------------------------------
    setPlanRule: {
      method: 'PUT',
      path: '/plans/:id/rules/:leaveTypeId',
      pathParams: planRuleParams,
      body: setLeavePlanRuleBodySchema,
      responses: {
        200: leavePlanRuleSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** `validFrom` at or before the open row's own — history is never rewritten. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Set a plan’s rule for a leave type from a date: closes the open row the day before, opens a new one (leave.plan:manage)',
      metadata: routeMeta('leave.plan:manage'),
    },

    // ---- Assignments --------------------------------------------------------------------
    listPlanAssignments: {
      method: 'GET',
      path: '/plan-assignments',
      query: listLeavePlanAssignmentsQuerySchema,
      responses: {
        200: paginatedSchema(leavePlanAssignmentSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Who each plan is aimed at, as of a date (or every row, closed ones included)',
      metadata: routeMeta('leave.plan:read'),
    },

    setPlanAssignment: {
      method: 'PUT',
      path: '/plan-assignments',
      body: setLeavePlanAssignmentBodySchema,
      responses: {
        200: setLeavePlanAssignmentResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** The plan, department, location or grade does not exist in this organisation. */
        404: errorBodySchema,
        /** `validFrom` at or before the open row's own — history is never rewritten. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Aim a plan at a department, location, grade, employment type or everyone from a date, or stop doing so (leave.plan:manage)',
      metadata: routeMeta('leave.plan:manage'),
    },

    // ---- Blackout periods (Phase 4c) ----------------------------------------------------
    listBlackoutPeriods: {
      method: 'GET',
      path: '/blackouts',
      query: listLeaveBlackoutPeriodsQuerySchema,
      responses: {
        200: paginatedSchema(leaveBlackoutPeriodSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Windows in which leave cannot be requested, in force now (or all, lifted ones included)',
      metadata: routeMeta('leave.plan:read'),
    },

    createBlackoutPeriod: {
      method: 'POST',
      path: '/blackouts',
      body: createLeaveBlackoutPeriodBodySchema,
      responses: {
        201: leaveBlackoutPeriodSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** The department or location does not exist in this organisation. */
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Block leave requests for days in a window, for everyone, a department or a location (leave.plan:manage)',
      metadata: routeMeta('leave.plan:manage'),
    },

    /** Lifts a blackout: the record is closed (validTo = today), never deleted — history stays. */
    endBlackoutPeriod: {
      method: 'DELETE',
      path: '/blackouts/:id',
      pathParams: blackoutParams,
      responses: {
        200: endLeaveBlackoutPeriodResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Already lifted. */
        409: errorBodySchema,
      },
      summary: 'Lift a blackout from today; the row is kept as history (leave.plan:manage)',
      metadata: routeMeta('leave.plan:manage'),
    },

    // ---- Minimum staffing (Phase 4c) ----------------------------------------------------
    listMinStaffingRules: {
      method: 'GET',
      path: '/min-staffing',
      query: listLeaveMinStaffingRulesQuerySchema,
      responses: {
        200: paginatedSchema(leaveMinStaffingRuleSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Minimum staffing rules in force on a date (or every row, closed ones included)',
      metadata: routeMeta('leave.plan:read'),
    },

    setMinStaffingRule: {
      method: 'PUT',
      path: '/min-staffing',
      body: setLeaveMinStaffingRuleBodySchema,
      responses: {
        200: setLeaveMinStaffingRuleResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** The department or location does not exist in this organisation. */
        404: errorBodySchema,
        /** `validFrom` at or before the open row's own — history is never rewritten. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Require a minimum number or share of people present for everyone, a department or a location from a date, or stop requiring it (leave.plan:manage)',
      metadata: routeMeta('leave.plan:manage'),
    },

    // ---- Month grid (Phase 4c) ----------------------------------------------------------
    getLeaveMonthCalendar: {
      method: 'GET',
      path: '/calendar/month',
      query: getLeaveMonthCalendarQuerySchema,
      responses: {
        200: leaveMonthCalendarSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'One month, day by day: leave counts, holidays, blackouts and staffing breaches for the organisation or one department / location',
      metadata: routeMeta('leave.plan:read'),
    },
  },
  { pathPrefix: '/leave' },
);
